import React from "react";
import { ChatsCircle, Van, ClipboardText, CurrencyDollar, Receipt, Timer } from "@phosphor-icons/react";
import { AnimatedValue, Delta } from "./CcPanel";
import { C, PERIODS, aud, num } from "./ccUtils";

const tp = (d, k) => d.tech.by_period[k];
const wp = (d, k) => d.warranty.by_period[k];
const ap = (d, k) => d.ai.automation.by_period[k];

const CARDS = [
  { id: "chats", label: "Chats with Nick", icon: ChatsCircle, tone: C.green,
    val: (d, k) => num(tp(d, k).chats), sub: (d, k) => `${tp(d, k).voice_share}% voice` },
  { id: "caravans", label: "Caravans helped", icon: Van, tone: C.green,
    val: (d, k) => num(tp(d, k).caravans), sub: (d, k) => `${num(tp(d, k).questions)} questions`,
    aside: (d) => d.tech.median_reply_s != null && <span className="text-[10px] font-mono text-neutral-500">{d.tech.median_reply_s}s reply</span> },
  { id: "claims", label: "Warranty claims received", icon: ClipboardText, tone: C.sky,
    val: (d, k) => num(wp(d, k).new), sub: (d, k) => `${num(wp(d, k).decided)} decided`,
    aside: (d) => <Delta v={d.warranty.new_7d_change_pct} label="7d" testid="kpi-claims-delta" /> },
  { id: "approved-value", label: "Approved warranty value", icon: CurrencyDollar, tone: C.gold, gold: true,
    val: (d, k) => aud(wp(d, k).approved_value), sub: (d, k) => `${num(wp(d, k).approved)} approved` },
  { id: "avg-cost", label: "Average approved claim", icon: Receipt, tone: C.gold, gold: true,
    val: (d, k) => aud(wp(d, k).avg_cost), sub: (d, k) => `${num(wp(d, k).declined)} declined`,
    aside: (d) => <Delta v={d.warranty.avg_cost_30d_change_pct} label="30d" testid="kpi-avg-cost-delta" /> },
  { id: "labour-saved", label: "Automation savings", icon: Timer, tone: C.green, gold: true,
    val: (d, k) => aud(ap(d, k).labour_saved), sub: (d, k) => `${ap(d, k).hours_saved} hrs saved`,
    aside: (d) => <span className="text-[10px] font-mono text-neutral-500" data-testid="kpi-labour-rate">@ {aud(d.ai.automation.rate, true)}/hr</span> },
];

function KpiCard({ card, data }) {
  const Icon = card.icon;
  return (
    <div data-testid={`kpi-${card.id}`} className="relative rounded-xl border border-neutral-800 bg-[#0F0F0F] px-4 pt-3 pb-2.5 overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-[2px] opacity-70" style={{ background: card.tone }} />
      <div className="flex items-center gap-2 h-5">
        <Icon size={15} weight="duotone" style={{ color: card.tone }} className="shrink-0" />
        <span className="text-[11px] font-heading font-semibold uppercase tracking-wider text-neutral-300 truncate">{card.label}</span>
        <span className="ml-auto shrink-0">{card.aside?.(data)}</span>
      </div>
      <div className="mt-2 space-y-0.5">
        {PERIODS.map((p) => (
          <div key={p.key} className={`grid grid-cols-[64px_1fr_auto] items-center gap-2 rounded-md px-1.5 py-[2px] ${p.key === "all" ? "bg-white/[0.04]" : ""}`}>
            <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">{p.label}</span>
            <AnimatedValue value={card.val(data, p.key)} testid={`kpi-${card.id}-${p.key}`}
              className="font-heading text-[19px] font-bold tabular-nums leading-tight" style={{ color: card.gold ? C.gold : C.white }} />
            <span className="text-[10px] text-neutral-500 truncate">{card.sub(data, p.key)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function KpiStrip({ data }) {
  return (
    <div data-testid="kpi-overview-strip" className="grid grid-cols-6 gap-3 shrink-0">
      {CARDS.map((c) => <KpiCard key={c.id} card={c} data={data} />)}
    </div>
  );
}
